import {
  IonBadge,
  IonIcon,
  IonLabel,
  IonRouterOutlet,
  IonTabBar,
  IonTabButton,
  IonTabs,
  useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { Redirect, Route } from "react-router-dom";
import {
  useFeatureFlags,
  useFeatureFlagsSettled,
} from "@/lib/backend/utils/hooks/use-feature-flags";
import { useAppReworkFlags } from "@/lib/hooks/use-app-rework-flags";
import { useAuthType } from "@/lib/hooks/use-auth-type";
import { usePreferredHaptics } from "@/lib/hooks/use-preferred-haptics";
import { useTabAlerts } from "@/lib/hooks/use-tab-alerts";
import { useTabBarVisibility } from "@/lib/hooks/use-tab-bar-visibility";
import { calendarIcon } from "@/lib/icons/calendar.icon";
import { homeIcon } from "@/lib/icons/home.icon";
import { searchIcon } from "@/lib/icons/search.icon";
import { userIcon } from "@/lib/icons/user.icon";
import { Routes } from "@/lib/router";
import { CompletedShiftsPage } from "@/routes/app/completed/completed";
import { CompletedShiftsReworkPage } from "@/routes/app/completed/completed.rework";
import { ProjectsPage } from "@/routes/app/project/projects";
import { ProjectsReworkPage } from "@/routes/app/project/projects.rework";
import { SettingsPage } from "@/routes/app/settings";
import { ShiftDetailPage } from "@/routes/app/shift/[id]";
import { TodayPage } from "@/routes/app/today/today";
import { TransferClaimPage } from "@/routes/app/transfer/[token]";
import { UpcomingShiftsPage } from "@/routes/app/upcoming/upcoming";
import { UpcomingShiftsReworkPage } from "@/routes/app/upcoming/upcoming.rework";
import SwitchOrganization from "@/routes/switch-organization";
import { ProjectRouter } from "./project.router";
import { RouteGuardPending } from "./route-guard";

export function TabRouter() {
  const { t } = useTranslation();
  const router = useIonRouter();
  const PreferredHaptics = usePreferredHaptics();
  const { isAdmin } = useAuthType();
  const { data: featureFlags } = useFeatureFlags();
  // Which half of the app this organization gets. This is the seam: every reworked screen is
  // chosen here, at the route, so neither version has to know the other exists.
  const rework = useAppReworkFlags();
  // Of er al een antwoord is. Alleen de twee sprongen hieronder wachten erop; schermen
  // renderen gewoon door met "uit", want die draaien vanzelf bij.
  const flagsSettled = useFeatureFlagsSettled();

  // Welke tabbladen een bolletje krijgen. Zie use-tab-alerts voor waarom het er maar twee zijn.
  const alerts = useTabAlerts();

  // Use the reusable tab bar visibility hook
  const hideTabBar = useTabBarVisibility();

  // Handler to reset tab history when clicking tab buttons
  const handleTabClick = (route: string) => {
    PreferredHaptics.impact();
    // Use 'root' to clear the entire tab history stack and start fresh
    router.push(route, "root", "replace");
  };

  return (
    <IonTabs>
      <IonRouterOutlet>
        {/* Organization switching route - should come first (most specific) */}
        <Route exact path="/app/switch-organization" component={SwitchOrganization} />

        {/* Project routes should come first (more specific) - redirect admins */}
        <Route
          path={Routes.Project.pattern}
          render={(routeProps) => {
            if (isAdmin) {
              return <Redirect to={Routes.Settings()} />;
            }
            return <ProjectRouter {...routeProps} />;
          }}
        />

        {/* Main tab routes - redirect admins to settings */}
        {/* Vandaag stays routable with the flag off so a deep link, a push or a stale history
            entry cannot land on a dead url; without the flag it sends you to the tab bar's
            first destination instead of rendering a tab nobody can see. */}
        <Route
          exact
          path={Routes.Today()}
          render={() => {
            if (isAdmin) {
              return <Redirect to={Routes.Settings()} />;
            }
            // Wegsturen kan maar één keer, dus niet doen zolang we niet weten of Vandaag
            // aanstaat: een deeplink of een push die hier binnenkomt terwijl de vlaggen nog
            // onderweg zijn belandde anders op Ontdek en bleef daar.
            if (!flagsSettled) {
              return <RouteGuardPending />;
            }

            return rework.today ? <TodayPage /> : <Redirect to={Routes.Projects()} />;
          }}
        />
        <Route
          exact
          path={Routes.Projects()}
          render={() => {
            if (isAdmin) {
              return <Redirect to={Routes.Settings()} />;
            }
            return rework.discover ? <ProjectsReworkPage /> : <ProjectsPage />;
          }}
        />
        <Route
          exact
          path={Routes.UpcomingShifts()}
          render={() => {
            if (isAdmin) {
              return <Redirect to={Routes.Settings()} />;
            }
            return rework.shifts ? <UpcomingShiftsReworkPage /> : <UpcomingShiftsPage />;
          }}
        />
        <Route
          exact
          path={Routes.CompletedShifts()}
          render={() => {
            if (isAdmin) {
              return <Redirect to={Routes.Settings()} />;
            }
            return rework.completed ? <CompletedShiftsReworkPage /> : <CompletedShiftsPage />;
          }}
        />

        {/* Singular /app/shift/:id — see Routes.ShiftDetail. Declaration order is not what
            protects the two /app/shifts/* list routes here; IonRouterOutlet keeps the LAST
            matching child, not the first. The separate segment does.

            Deliberately not behind a flag. It is a new segment that nothing in the legacy app
            links to, so it is inert while the flags are off — and registering it unconditionally
            is what lets Vandaag open a shift while Diensten is still the old screen, and keeps a
            deep link from dying the moment someone flips a flag back. */}
        <Route
          exact
          path={Routes.ShiftDetail.pattern}
          render={() => {
            if (isAdmin) {
              return <Redirect to={Routes.Settings()} />;
            }
            return <ShiftDetailPage />;
          }}
        />

        {/* Transfer claim route */}
        <Route
          exact
          path={Routes.TransferClaim.pattern}
          render={() => {
            if (isAdmin) {
              return <Redirect to={Routes.Settings()} />;
            }
            return <TransferClaimPage />;
          }}
        />

        <Route exact path={Routes.Settings()} component={SettingsPage} />

        {/* With Vandaag on, an employee opening the app lands there — the day question beats the
            marketplace question. Without it, /app resolves where it always did. Admins keep their
            existing destination either way.

            Dit is de enige plek waar het beginscherm gekozen wordt, en hij wacht op de vlaggen.
            Kiezen terwijl ze nog onderweg zijn betekende kiezen met "alles uit", en die sprong
            valt één keer: je stond op Ontdek en bleef daar, ook als Vandaag gewoon aanstond. Een
            scherm dat even de oude versie toont draait bij, een sprong niet. */}
        <Route
          exact
          path={Routes.App()}
          render={() => {
            if (isAdmin) {
              return <Redirect to={Routes.Settings()} />;
            }

            if (!flagsSettled) {
              return <RouteGuardPending />;
            }

            return <Redirect to={rework.today ? Routes.Today() : Routes.Projects()} />;
          }}
        />

        {/*
          Het vangnet, en de reden dat het geen `path` heeft.

          `IonRouterOutlet` is geen `Switch`: `matchRoute` loopt alle kinderen langs en houdt de
          laatste match over, dus een `path="*"` zou elke route hierboven overrulen. Een kind
          zonder `path` en zonder `from` behandelt Ionic juist als de niet-gevonden route: dat
          kind komt alleen aan de beurt als niets anders matchte (`matchDefaultRoute` in
          `@ionic/react-router`).

          "Niets anders" telt per outlet, en dat is waarom dit hier ook moet staan en niet alleen
          in de globale router. Alles onder `/app` matcht daar `Routes.App()`, dat bewust niet
          `exact` is; die outlet heeft zijn match dus al en komt bij zijn eigen pathloze kind
          nooit uit. Een pad onder `/app` zonder route hier rendert daardoor de tabbalk met een
          lege inhoud eronder -- geen wit scherm zoals daar, maar net zo dood.

          Dat is bereikbaar en geen theorie: een pushmelding draagt haar eigen pad, en de
          meldingen die naar een scherm uit het opgeruimde communicatieblok wijzen zijn al
          bezorgd. Zie `lib/router/communicatie.guard.test.ts` voor wat er weg is. `/app` weet
          zelf al waar iemand hoort uit te komen, dus die keuze blijft op één plek staan.
        */}
        <Route render={() => <Redirect to={Routes.App()} />} />
      </IonRouterOutlet>

      <IonTabBar
        slot="bottom"
        // De ruimte onder de labels komt uit `styles/ionic/overrides/tab-bar.css` en niet uit een
        // `pb-8` hier: die was afgemeten aan één toestel mét home-indicator, en stond dus twee
        // centimeter te ruim op een SE en te krap op een Android met gebarenbalk.
        className={`border-t border-bordercolor-primary ${hideTabBar ? "hidden" : ""}`}
      >
        {!isAdmin && rework.today && (
          <IonTabButton
            tab="today"
            href={Routes.Today()}
            onClick={(e) => {
              e.preventDefault();
              handleTabClick(Routes.Today());
            }}
            className="group"
          >
            <IonIcon
              icon={homeIcon}
              className="size-6 text-txt-primary group-[.tab-selected]:text-brand-500"
            />
            <IonLabel className="text-xs font-medium leading-[1.5] text-txt-primary group-[.tab-selected]:text-brand-500">
              {t("routes:today.title")}
            </IonLabel>
          </IonTabButton>
        )}

        {!isAdmin && (
          <IonTabButton
            tab="projects"
            href={Routes.Projects()}
            onClick={(e) => {
              e.preventDefault();
              handleTabClick(Routes.Projects());
            }}
            className="group"
          >
            <IonIcon
              icon={searchIcon}
              className="size-6 text-txt-primary group-[.tab-selected]:text-brand-500"
            />
            <IonLabel className="text-xs font-medium leading-[1.5] text-txt-primary group-[.tab-selected]:text-brand-500">
              {t("routes:tabs.discover")}
            </IonLabel>
          </IonTabButton>
        )}

        {!isAdmin && (
          <IonTabButton
            tab="upcoming"
            href={Routes.UpcomingShifts()}
            onClick={(e) => {
              e.preventDefault();
              handleTabClick(Routes.UpcomingShifts());
            }}
            className="group"
          >
            <IonIcon
              icon={calendarIcon}
              className="size-6 text-txt-primary group-[.tab-selected]:text-brand-500"
            />
            {alerts.shifts && <TabAlertDot />}
            <IonLabel className="text-xs font-medium leading-[1.5] text-txt-primary group-[.tab-selected]:text-brand-500">
              {t("routes:upcoming-shifts.title")}
            </IonLabel>
          </IonTabButton>
        )}

        <IonTabButton
          tab="settings"
          href={Routes.Settings()}
          onClick={(e) => {
            e.preventDefault();
            handleTabClick(Routes.Settings());
          }}
          className="group"
        >
          <IonIcon
            icon={userIcon}
            className="size-6 text-txt-primary group-[.tab-selected]:text-brand-500"
          />
          <IonLabel className="text-xs font-medium leading-[1.5] text-txt-primary group-[.tab-selected]:text-brand-500">
            {t("routes:tabs.profile")}
          </IonLabel>
        </IonTabButton>

        {/* <IonTabButton
          tab="notifications"
          href={Routes.Settings()}
          onClick={(e) => {
            e.preventDefault();
            handleTabClick(Routes.Settings());
          }}
          className="group"
        >
          <IonIcon
            icon={notificationIcon}
            className="size-6 text-txt-primary group-[.tab-selected]:text-brand-500"
          />
          <IonLabel className="text-xs font-medium leading-[1.5] text-txt-primary group-[.tab-selected]:text-brand-500">
            Meldingen
          </IonLabel>
        </IonTabButton> */}
      </IonTabBar>
    </IonTabs>
  );
}

/**
 * Een bolletje op een tabblad: hier wacht iets op je.
 *
 * Een `ion-badge` en geen eigen `div`, omdat `ion-tab-button` er zelf een plek voor heeft --
 * rechtsboven het icoon, ook als de balk van hoogte verandert. De maat en de ronding komen uit
 * styles/index.css; hier zit alleen de tekst die niemand ziet, zodat een schermlezer wél hoort wat
 * het rode stipje betekent.
 */
function TabAlertDot() {
  const { t } = useTranslation();

  return (
    <IonBadge className="tab-alert-dot">
      <span className="sr-only">{t("routes:tabs.needs-attention")}</span>
    </IonBadge>
  );
}
